// A team's badge: initials, an icon the site draws, an emoji from a set the
// site chooses (DIRECTION U25, PLAN-2026-10-18-families-and-newcomers §11),
// or a face its manager builds from the site's parts (faces.ts, B-488).
//
// A FIXED SET, so there is nothing to upload, store or moderate, and a
// family league never meets a badge it would rather a child did not see: no
// weapons, nothing rude, no flags or symbols of anything. The ids are what a
// team stores and the wire carries; the server refuses one not here, and a
// client draws the icons in its own look (the award and achievement art each
// look already draws) and the emoji in the system's font.

import { z } from 'zod';
import { BOT_FACES } from './bot-faces';
import { Face } from './faces';

/** One choice: its id, what it is called aloud, and the emoji itself for an emoji. */
export interface BadgeChoice {
  id: string;
  /** What a screen reader says, and the picker's tooltip. */
  name: string;
}
export interface EmojiChoice extends BadgeChoice {
  emoji: string;
  /** The picker's shelf it sits on. */
  group: 'Animals' | 'Food' | 'Games' | 'Nature' | 'Faces' | 'Things';
}

/**
 * The icons a badge can be: drawings every look already has for an award or
 * an achievement, chosen to be a team's mark and nothing grim.
 */
export const BADGE_ICONS: readonly BadgeChoice[] = [
  { id: 'crown', name: 'Crown' },
  { id: 'trophy', name: 'Trophy' },
  { id: 'medal', name: 'Medal' },
  { id: 'star', name: 'Star' },
  { id: 'rocket', name: 'Rocket' },
  { id: 'flame', name: 'Flame' },
  { id: 'bolt', name: 'Lightning bolt' },
  { id: 'target', name: 'Target' },
  { id: 'shield', name: 'Shield' },
  { id: 'anchor', name: 'Anchor' },
  { id: 'mountain', name: 'Mountain' },
  { id: 'sparkles', name: 'Sparkles' },
  { id: 'ghost', name: 'Ghost' },
  { id: 'dog', name: 'Dog' },
  { id: 'bird', name: 'Feather' },
  { id: 'tree', name: 'Tree' },
  { id: 'moon', name: 'Moon' },
  { id: 'sunrise', name: 'Sunrise' },
  { id: 'island', name: 'Beach' },
  { id: 'house', name: 'House' },
  { id: 'cake', name: 'Cake' },
  { id: 'gift', name: 'Gift' },
  { id: 'heart', name: 'Heart' },
  { id: 'dice', name: 'Dice' },
  { id: 'compass', name: 'Compass' },
  { id: 'telescope', name: 'Telescope' },
  { id: 'crystal-ball', name: 'Crystal ball' },
  { id: 'magnet', name: 'Magnet' },
  { id: 'megaphone', name: 'Megaphone' },
  { id: 'palette', name: 'Palette' },
  { id: 'theater', name: 'Theater masks' },
];

export const BADGE_EMOJI: readonly EmojiChoice[] = [
  // Animals
  { id: 'puppy', emoji: '🐶', name: 'Puppy', group: 'Animals' },
  { id: 'kitten', emoji: '🐱', name: 'Kitten', group: 'Animals' },
  { id: 'fox', emoji: '🦊', name: 'Fox', group: 'Animals' },
  { id: 'bear', emoji: '🐻', name: 'Bear', group: 'Animals' },
  { id: 'panda', emoji: '🐼', name: 'Panda', group: 'Animals' },
  { id: 'koala', emoji: '🐨', name: 'Koala', group: 'Animals' },
  { id: 'tiger', emoji: '🐯', name: 'Tiger', group: 'Animals' },
  { id: 'lion', emoji: '🦁', name: 'Lion', group: 'Animals' },
  { id: 'cow', emoji: '🐮', name: 'Cow', group: 'Animals' },
  { id: 'pig', emoji: '🐷', name: 'Pig', group: 'Animals' },
  { id: 'frog', emoji: '🐸', name: 'Frog', group: 'Animals' },
  { id: 'monkey', emoji: '🐵', name: 'Monkey', group: 'Animals' },
  { id: 'chick', emoji: '🐥', name: 'Chick', group: 'Animals' },
  { id: 'penguin', emoji: '🐧', name: 'Penguin', group: 'Animals' },
  { id: 'eagle', emoji: '🦅', name: 'Eagle', group: 'Animals' },
  { id: 'owl', emoji: '🦉', name: 'Owl', group: 'Animals' },
  { id: 'wolf', emoji: '🐺', name: 'Wolf', group: 'Animals' },
  { id: 'horse', emoji: '🐴', name: 'Horse', group: 'Animals' },
  { id: 'unicorn', emoji: '🦄', name: 'Unicorn', group: 'Animals' },
  { id: 'bee', emoji: '🐝', name: 'Bee', group: 'Animals' },
  { id: 'ladybug', emoji: '🐞', name: 'Ladybug', group: 'Animals' },
  { id: 'butterfly', emoji: '🦋', name: 'Butterfly', group: 'Animals' },
  { id: 'turtle', emoji: '🐢', name: 'Turtle', group: 'Animals' },
  { id: 'dinosaur', emoji: '🦖', name: 'Dinosaur', group: 'Animals' },
  { id: 'octopus', emoji: '🐙', name: 'Octopus', group: 'Animals' },
  { id: 'crab', emoji: '🦀', name: 'Crab', group: 'Animals' },
  { id: 'dolphin', emoji: '🐬', name: 'Dolphin', group: 'Animals' },
  { id: 'whale', emoji: '🐳', name: 'Whale', group: 'Animals' },
  { id: 'shark', emoji: '🦈', name: 'Shark', group: 'Animals' },
  { id: 'giraffe', emoji: '🦒', name: 'Giraffe', group: 'Animals' },
  { id: 'elephant', emoji: '🐘', name: 'Elephant', group: 'Animals' },
  { id: 'sloth', emoji: '🦥', name: 'Sloth', group: 'Animals' },
  // Food
  { id: 'pizza', emoji: '🍕', name: 'Pizza', group: 'Food' },
  { id: 'burger', emoji: '🍔', name: 'Burger', group: 'Food' },
  { id: 'taco', emoji: '🌮', name: 'Taco', group: 'Food' },
  { id: 'fries', emoji: '🍟', name: 'Fries', group: 'Food' },
  { id: 'hot-dog', emoji: '🌭', name: 'Hot dog', group: 'Food' },
  { id: 'donut', emoji: '🍩', name: 'Donut', group: 'Food' },
  { id: 'cookie', emoji: '🍪', name: 'Cookie', group: 'Food' },
  { id: 'birthday-cake', emoji: '🎂', name: 'Birthday cake', group: 'Food' },
  { id: 'ice-cream', emoji: '🍦', name: 'Ice cream', group: 'Food' },
  { id: 'popcorn', emoji: '🍿', name: 'Popcorn', group: 'Food' },
  { id: 'pretzel', emoji: '🥨', name: 'Pretzel', group: 'Food' },
  { id: 'cheese', emoji: '🧀', name: 'Cheese', group: 'Food' },
  { id: 'bacon', emoji: '🥓', name: 'Bacon', group: 'Food' },
  { id: 'apple', emoji: '🍎', name: 'Apple', group: 'Food' },
  { id: 'banana', emoji: '🍌', name: 'Banana', group: 'Food' },
  { id: 'watermelon', emoji: '🍉', name: 'Watermelon', group: 'Food' },
  { id: 'strawberry', emoji: '🍓', name: 'Strawberry', group: 'Food' },
  { id: 'avocado', emoji: '🥑', name: 'Avocado', group: 'Food' },
  { id: 'hot-pepper', emoji: '🌶️', name: 'Hot pepper', group: 'Food' },
  // Games and sport, every sport's
  { id: 'trophy-cup', emoji: '🏆', name: 'Trophy', group: 'Games' },
  { id: 'gold-medal', emoji: '🥇', name: 'Gold medal', group: 'Games' },
  { id: 'bullseye', emoji: '🎯', name: 'Bullseye', group: 'Games' },
  { id: 'football', emoji: '🏈', name: 'Football', group: 'Games' },
  { id: 'soccer-ball', emoji: '⚽', name: 'Soccer ball', group: 'Games' },
  { id: 'basketball', emoji: '🏀', name: 'Basketball', group: 'Games' },
  { id: 'baseball', emoji: '⚾', name: 'Baseball', group: 'Games' },
  { id: 'hockey', emoji: '🏒', name: 'Hockey stick', group: 'Games' },
  { id: 'checkered-flag', emoji: '🏁', name: 'Checkered flag', group: 'Games' },
  { id: 'video-game', emoji: '🎮', name: 'Game controller', group: 'Games' },
  { id: 'die', emoji: '🎲', name: 'Die', group: 'Games' },
  { id: 'puzzle', emoji: '🧩', name: 'Puzzle piece', group: 'Games' },
  // Nature
  { id: 'glowing-star', emoji: '🌟', name: 'Glowing star', group: 'Nature' },
  { id: 'rainbow', emoji: '🌈', name: 'Rainbow', group: 'Nature' },
  { id: 'sun', emoji: '☀️', name: 'Sun', group: 'Nature' },
  { id: 'crescent', emoji: '🌙', name: 'Crescent moon', group: 'Nature' },
  { id: 'lightning', emoji: '⚡', name: 'Lightning', group: 'Nature' },
  { id: 'fire', emoji: '🔥', name: 'Fire', group: 'Nature' },
  { id: 'snowflake', emoji: '❄️', name: 'Snowflake', group: 'Nature' },
  { id: 'wave', emoji: '🌊', name: 'Wave', group: 'Nature' },
  { id: 'cactus', emoji: '🌵', name: 'Cactus', group: 'Nature' },
  { id: 'evergreen', emoji: '🌲', name: 'Evergreen', group: 'Nature' },
  { id: 'clover', emoji: '🍀', name: 'Four-leaf clover', group: 'Nature' },
  { id: 'sunflower', emoji: '🌻', name: 'Sunflower', group: 'Nature' },
  { id: 'blossom', emoji: '🌸', name: 'Blossom', group: 'Nature' },
  { id: 'maple-leaf', emoji: '🍁', name: 'Maple leaf', group: 'Nature' },
  // Faces
  { id: 'cool', emoji: '😎', name: 'Cool face', group: 'Faces' },
  { id: 'cowboy', emoji: '🤠', name: 'Cowboy', group: 'Faces' },
  { id: 'party', emoji: '🥳', name: 'Party face', group: 'Faces' },
  { id: 'star-struck', emoji: '🤩', name: 'Star-struck', group: 'Faces' },
  { id: 'nerd', emoji: '🤓', name: 'Nerd face', group: 'Faces' },
  { id: 'robot', emoji: '🤖', name: 'Robot', group: 'Faces' },
  { id: 'friendly-ghost', emoji: '👻', name: 'Ghost', group: 'Faces' },
  { id: 'alien', emoji: '👽', name: 'Alien', group: 'Faces' },
  { id: 'cold', emoji: '🥶', name: 'Cold face', group: 'Faces' },
  { id: 'silly', emoji: '😜', name: 'Silly face', group: 'Faces' },
  // Things
  { id: 'rocket-launch', emoji: '🚀', name: 'Rocket', group: 'Things' },
  { id: 'guitar', emoji: '🎸', name: 'Guitar', group: 'Things' },
  { id: 'top-hat', emoji: '🎩', name: 'Top hat', group: 'Things' },
  { id: 'gem', emoji: '💎', name: 'Gem', group: 'Things' },
  { id: 'teddy-bear', emoji: '🧸', name: 'Teddy bear', group: 'Things' },
  { id: 'balloon', emoji: '🎈', name: 'Balloon', group: 'Things' },
  { id: 'confetti', emoji: '🎉', name: 'Party popper', group: 'Things' },
  { id: 'ufo', emoji: '🛸', name: 'Flying saucer', group: 'Things' },
  { id: 'castle', emoji: '🏰', name: 'Castle', group: 'Things' },
  { id: 'anchor-emoji', emoji: '⚓', name: 'Anchor', group: 'Things' },
  { id: 'crown-emoji', emoji: '👑', name: 'Crown', group: 'Things' },
  { id: 'magic-wand', emoji: '🪄', name: 'Magic wand', group: 'Things' },
];

/** A badge from one of the site's sets, by its id: an icon, an emoji, or a bot's face. */
const SetBadge = z.object({
  kind: z.enum(['icon', 'emoji', 'bot']),
  id: z.string().min(1).max(40),
});

/** A face its manager built (B-488): the parts, the colors and the steps themselves. */
export const FaceBadge = z.object({ kind: z.literal('face'), face: Face });
export type FaceBadge = z.infer<typeof FaceBadge>;

/**
 * A team's badge, on the wire and as stored (`teams.badge_json`): an icon or
 * an emoji by its id, a bot's face (`bot`, its id the character's,
 * `BOT_FACES`), which a seat a bot holds wears (AI_MANAGERS.md §5.4, B-464;
 * added 2026-10-20), or a face a person built (`face`, B-488; added
 * 2026-10-24). Null is the team's initials, which every team had before
 * badges.
 */
export const TeamBadge = z.union([SetBadge, FaceBadge]).nullable();
export type TeamBadge = z.infer<typeof TeamBadge>;

/**
 * A badge a person chooses for their team, and the open league format's: an
 * icon, an emoji or a face they built. A character's face is the house's to
 * give a bot's seat, never chosen; a person may build one like it.
 */
export const ChosenBadge = z
  .union([z.object({ kind: z.enum(['icon', 'emoji']), id: z.string().min(1).max(40) }), FaceBadge])
  .nullable();
export type ChosenBadge = z.infer<typeof ChosenBadge>;

/** Whether a badge is one of the site's, as the server checks before storing it. */
export function isKnownBadge(badge: NonNullable<TeamBadge>): boolean {
  if (badge.kind === 'face') return Face.safeParse(badge.face).success;
  if (badge.kind === 'bot') return Object.hasOwn(BOT_FACES, badge.id);
  return badge.kind === 'icon'
    ? BADGE_ICONS.some((b) => b.id === badge.id)
    : BADGE_EMOJI.some((b) => b.id === badge.id);
}

/** A badge a person may have chosen: a bot's face is not one, and reads as none. */
export function chosenBadge(badge: TeamBadge): ChosenBadge {
  if (badge === null || badge.kind === 'bot') return null;
  return badge.kind === 'face' ? badge : { kind: badge.kind, id: badge.id };
}

/** Two badges the same, a face part for part: what Edit team's Save compares. */
export function sameBadge(a: TeamBadge, b: TeamBadge): boolean {
  // Through the schema, so a face's keys are in one order whoever built it.
  const norm = (x: TeamBadge) => JSON.stringify(TeamBadge.safeParse(x).data ?? x);
  return norm(a) === norm(b);
}

/** The face a seat a bot takes wears, as stored; null for a character with none. */
export function botBadgeJson(presetId: string | null | undefined): string | null {
  return presetId != null && Object.hasOwn(BOT_FACES, presetId)
    ? JSON.stringify({ kind: 'bot', id: presetId } satisfies TeamBadge)
    : null;
}

/** The emoji a badge draws, or null for an icon or an id not in the set. */
export function badgeEmoji(badge: TeamBadge): string | null {
  if (badge === null || badge.kind !== 'emoji') return null;
  return BADGE_EMOJI.find((b) => b.id === badge.id)?.emoji ?? null;
}

/** What a badge is called aloud: *Fox*, *Crown*; null for initials. */
export function badgeName(badge: TeamBadge): string | null {
  if (badge === null || badge.kind === 'bot') return null;
  if (badge.kind === 'face') return 'Face';
  const list: readonly BadgeChoice[] = badge.kind === 'icon' ? BADGE_ICONS : BADGE_EMOJI;
  return list.find((b) => b.id === badge.id)?.name ?? null;
}
